<script>
import Card from '@/components/ui/Card.vue';
import chartColors from '@/components/common/chart-colors';
import AdvertisementLineChart from '../common/AdvertisementLineChart.vue';
import ChartSeriesGuide from '@/components/common/ChartSeriesGuide.vue';
import ChartSelector from './ChartSelector.vue';
import ChartSeriesTypeSelect from './ChartSeriesTypeSelect.vue';

export default {
  name: 'ReportingChart',
  components: {
    AdvertisementLineChart,
    Card,
    ChartSeriesGuide,
    ChartSelector,
    ChartSeriesTypeSelect,
  },
  props: {
    stats: {
      type: Array,
      default: () => [],
    },
    campaigns: {
      type: Array,
      default: () => [],
    },
    xAxis: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showRealScale: false,
      activeSeries: [0],
      primaryActiveStat: null,
      secondaryActiveStat: null,
      seriesConfigs: [],
      yAxisConfig: [],
      chartType: 'overall',
    };
  },
  computed: {
    config() {
      return {
        xAxis: { categories: this.xAxis },
        series: [...this.seriesConfigs],
        yAxis: [...this.yAxisConfig],
      };
    },
    chartGuideItems() {
      return this.primaryActiveStat?.series.map((series, index) => ({
        name: series.name,
        color: chartColors[index],
      }));
    },
  },
  watch: {
    stats: {
      immediate: true,
      handler(stats) {
        if (!stats?.length) return;
        this.primaryActiveStat = stats[0];
        this.secondaryActiveStat = stats[1];
        this.updateChartSeries();
      },
    },
    showRealScale(value) {
      if (value) {
        this.seriesConfigs.forEach(ser => {
          if (ser.dashStyle === 'dash') ser.yAxis = 0;
        });
      } else {
        this.seriesConfigs.forEach(ser => {
          if (ser.dashStyle === 'dash') ser.yAxis = 1;
        });
      }
    },
    chartType() {
      this.updateChartSeries();
    },
  },
  methods: {
    selectStat(stat, index) {
      if (index === 1) {
        this.primaryActiveStat = stat;
      } else if (index === 2) {
        this.secondaryActiveStat = stat;
      }
      this.updateChartSeries();
    },
    updateChartSeries() {
      const chartKey =
        this.chartType === 'overall' ? 'overallSeries' : 'series';

      const solidSeries = this.primaryActiveStat?.[chartKey]?.map(
        (seriesData, i) => ({
          ...seriesData,
          visible: true,
          color: chartColors[i],
          yAxis: 0,
          dashStyle: 'solid',
          name: this.primaryActiveStat?.name,
          description: seriesData.name,
        }),
      );

      const dashedSeries = this.secondaryActiveStat?.[chartKey]?.map(
        (seriesData, i) => ({
          ...seriesData,
          visible: true,
          color: chartColors[i],
          yAxis: 1,
          dashStyle: 'dash',
          name: this.secondaryActiveStat?.name,
          description: seriesData.name,
        }),
      );

      this.seriesConfigs = [...solidSeries, ...dashedSeries];
      this.yAxisConfig = [
        { title: { text: this.primaryActiveStat?.name } },
        { title: { text: this.secondaryActiveStat?.name }, opposite: true },
      ];
    },
  },
};
</script>

<template>
  <Card class="reporting-chart" :mask-loading="loading">
    <div class="flex-row flex-wrap justify-spaced mb-2">
      <div class="flex-row flex-wrap gap-md ">
        <ChartSelector
          :charts="stats"
          :active-chart="primaryActiveStat"
          :icon="require('@/assets/images/solid-chart.svg')"
          @change="selectStat($event, 1)"
        />
        <ChartSelector
          :charts="stats"
          :active-chart="secondaryActiveStat"
          :icon="require('@/assets/images/dashed-chart.svg')"
          @change="selectStat($event, 2)"
        />
      </div>

      <div class="flex-row flex-wrap align-middle gap-md">
        <div>{{ $t('ADVERTISEMENT.REPORTING.CHART_SERIES_MODE') }}</div>
        <ChartSeriesTypeSelect v-model="chartType" />
      </div>
    </div>

    <AdvertisementLineChart :config="config" />
    <ChartSeriesGuide
      v-show="chartType !== 'overall'"
      :series="chartGuideItems"
      class="mt"
    />
  </Card>
</template>

<style lang="scss">
.reporting-chart {
  .chart-series-item {
    border: 1px solid #eee;
    border-radius: 16px;
    background: #fff;
    max-width: 200px;
    padding: 0.5rem;
    cursor: pointer;
    display: flex;
    flex: 1 0 0;
    align-items: center;
    gap: rem-calc(18px);
    transition: all ease 0.3s;

    @include breakpoint(medium down) {
      flex-basis: 45% !important;
      padding: 0.3rem 0.8rem;
    }

    &--first-selection {
      border-color: #20222a;
      // background-color: #f1f2f9;
    }

    &--second-selection {
      border-color: #20222a;
      // background-color: #f1f2f9;
      border-style: dashed;
    }

    &__icon {
      width: 50px;
      height: 50px;
      line-height: 50px;
      text-align: center;
      font-size: 1.2rem;
      flex-shrink: 0;
      background-color: #f1f2f9;
      color: #3e4151;

      border-radius: 50%;
      transition: all ease 0.3s;
    }

    &--first-selection .chart-series-item__icon {
      background-color: #eee !important;
      color: #20222a !important;
    }

    &--second-selection .chart-series-item__icon {
      background-color: #eee !important;
      color: #20222a !important;
    }
    // &:hover &__icon {
    //   background-color: #ecfcf4;
    //   color: #0ea960;
    // }

    &__title {
      color: #717485;
      font-weight: 500;
      font-size: 0.8rem;
    }

    &__data {
      font-size: 1.2rem;
      color: #20222a;
      font-weight: 400;
    }
  }
}

.chart-series {
  margin-bottom: 2.5rem;
  display: flex;
  gap: 1.2rem;
  flex-wrap: wrap;
}
</style>
